import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { usePagination } from '@webapp/core/hooks/usePagination'
import { ReconcileMatch } from '@webapp/modules/accounts-payable/entities/ReconcileMatch'
import { reconcileSessionQueries } from '@webapp/modules/accounts-payable/query-configs/reconcileSessionQueries'

interface IUseQueryBillMatchedItemsParams {
  sessionId: number
  onError?: (err: unknown) => void
}

interface IUseQueryBillMatchedItemsReturn {
  matches: ReconcileMatch[]
  /** metadata.summary.total_amount — "Total cost" line (BHV-050) */
  totalCost: Nullable<number>
  isLoading: boolean
  error: unknown
}

// Shares `reconcileSessionQueries.matches` with feature 16 (deduplicated
// cache, ADR-5).
export const useQueryBillMatchedItems = ({
  sessionId,
  onError,
}: IUseQueryBillMatchedItemsParams): IUseQueryBillMatchedItemsReturn => {
  // Reuses the matched-tab pagination id so params (and therefore the
  // `reconcileSessionQueries.matches` cache key) are shared with feature 16.
  const { params, updateNumPages } = usePagination({
    id: 'ap-reconcile-matched',
  })

  const { data, isLoading, error } = useQuery({
    ...reconcileSessionQueries.matches(sessionId, {
      page: params.page,
      pageSize: params.pageSize,
    }),
    // Skip the fetch until a real session resolves — the FS passes `0` before
    // the account/session loads, which would 404 and toast (BHV context).
    enabled: sessionId > 0,
    onSuccess: (res) => {
      updateNumPages(res.pagination.numPages)
    },
    onError,
  })

  const matches = useMemo(
    () =>
      (data?.matches ?? []).map(
        (constructor) => new ReconcileMatch(constructor)
      ),
    [data]
  )

  return {
    matches,
    totalCost: data?.totalAmount ?? null,
    isLoading,
    error,
  }
}
